import {authorize} from 'authorize';
import EvaluationTimes from 'constants/LU_EvaluationTimes';
import sessionsMessages from 'intl/sessions-messages';
import every from 'lodash/every';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import negate from 'lodash/negate';
import split from 'lodash/split';
import startsWith from 'lodash/startsWith';

import type {NavigationItem} from 'components/sidebar-navigation-bar/sidebar-navigation-bar';
import type {Location} from 'history';

export const getVisibleNavItems = function (navItems: Readonly<NavigationItem[]>, session): NavigationItem[] {
    return navItems
        .map<NavigationItem>((navItem) => {
            const children = navItem.children;

            if (isNil(children)) {
                return navItem;
            } else {
                return {
                    ...navItem,
                    children: getVisibleNavItems(children, session),
                };
            }
        })
        .filter((navItem) => {
            if (typeof navItem.isVisible === 'function') {
                return navItem.isVisible(session);
            } else {
                return true;
            }
        });
};

/**
 * Generates a function that returns true if the logged in user has the
 * specified permissions on the session.
 */
export const navItemIsVisibleWithPermissions = function (permissions?) {
    return function (session) {
        let isVisible = false;
        if (isNil(permissions)) {
            isVisible = true; // item is not locked by permission
        } else {
            isVisible = authorize(permissions, session);
        }

        return isVisible;
    };
};

/**
 * The evaluations tab is accessible as long as their's a scenario on the session.
 */
export const evaluationsNavItemIsVisible = function (session) {
    return !isNil(session.get('scenarioId'));
};

/**
 * Matches a location with a set of path patterns representing the sidebar
 * items. Returns true if one of the patterns matches the location.
 *
 * Note that locations contain concrete url parameter values, e.g. resource
 * uuids, whereas path patterns contain the names of the parameters, e.g.
 * `:userId`.
 *
 * Also note that location lengths can vary depending on where the view is
 * being served from (in the AWS dev build environment, each branch has its
 * own subpath, which lengthens the location. For purposes of this
 * validation function, we only care about matching the end of the url.
 */
export const getCanViewSidebar = function (
    location: Location,
    navItems: Readonly<NavigationItem[]>,
    parentPath: string | null = null,
) {
    if (isNil(navItems) || navItems.length === 0) {
        return false;
    }

    return navItems.some((navItem) => {
        let matched: boolean;
        if (navItemMatchesLocation(navItem, location, parentPath)) {
            matched = true;
        } else if (!isNil(navItem.children)) {
            matched = getCanViewSidebar(location, navItem.children, navItem.path);
        }
        return matched;
    });
};

function navItemMatchesLocation(navItem: NavigationItem, location: Location, parentPath: string | null) {
    const locationSegments = getPathSegments(location.pathname);
    const navItemSegments = getPathSegments(navItem.path);
    if (!isNil(parentPath)) {
        navItemSegments.unshift(parentPath);
    }

    const offset = locationSegments.length - navItemSegments.length;
    let matched: boolean;
    if (offset < 0) {
        matched = false;
    } else {
        matched = every(navItemSegments, (navItemSegment, i) => {
            return navItemSegment === locationSegments[i + offset] || startsWith(navItemSegment, ':');
        });
    }

    return matched;
}

function getPathSegments(path: string) {
    return split(path, '/').filter(negate(isEmpty));
}

export const getInProgressFormTitleMessage = (form, formType) => {
    if (formType === EvaluationTimes.REFLECTION) {
        return sessionsMessages.inProgressReflectionTitle;
    } else {
        // is evaluation
        return form.get('isTeamEvaluation')
            ? sessionsMessages.inProgressEvaluationTitleTeam
            : sessionsMessages.inProgressEvaluationTitle;
    }
};

export const getCompletedFormTitleMessage = (form, formType) => {
    if (formType === EvaluationTimes.REFLECTION) {
        return sessionsMessages.completedReflectionTitle;
    } else {
        // is evaluation
        return form.get('isTeamEvaluation')
            ? sessionsMessages.completedEvaluationTitleTeam
            : sessionsMessages.completedEvaluationTitle;
    }
};
